// client/src/components/MerchantDocs.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { firestore, storage } from '../../firebase';
import {
  doc,
  collection,
  collectionGroup,
  query,
  where,
  getDocs,
  onSnapshot,
  limit as fsLimit,
} from 'firebase/firestore';

import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { ref, getDownloadURL, listAll, getMetadata, uploadBytes } from 'firebase/storage';
import '../CSS/Dashboard.css';
import { useNavigate } from 'react-router-dom';
import MerchantNavbar from './MerchantNavbar';
import { useDataContext } from '../../DataContext';
import { FaUpload } from 'react-icons/fa';
import { listCompanyShares } from '../../utils/shareStore';

// =======================================================
// Composant pour récupérer et afficher le logo du commerçant (avec cache)
// (même rendu que dans ClientDocs : l'image remplit l'avatar)
// =======================================================
const MerchantLogo = ({ merchantId }) => {
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    if (!merchantId) return;

    const TTL = 5 * 60 * 1000; // 5 minutes
    const cacheKey = `merchantLogo_${merchantId}`;
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        // Utilise l'URL si cache récent
        if (parsed?.url && parsed?.timestamp && Date.now() - parsed.timestamp < TTL) {
          setLogoUrl(parsed.url);
        } else if (parsed?.url) {
          // on peut afficher l'ancien le temps de rafraîchir en arrière-plan
          setLogoUrl(parsed.url);
        }
      } catch {}
    }

    let mounted = true;
    (async () => {
      try {
        const logoRef = ref(storage, `${merchantId}/photo-profil/`);
        const logoList = await listAll(logoRef);

        if (logoList.items.length > 0) {
          const logosWithMetadata = await Promise.all(
            logoList.items.map(async (item) => {
              const metadata = await getMetadata(item).catch(() => null);
              const timeCreated = metadata?.timeCreated ? new Date(metadata.timeCreated) : new Date(0);
              return { item, timeCreated };
            })
          );
          logosWithMetadata.sort((a, b) => b.timeCreated - a.timeCreated);
          const latestLogo = logosWithMetadata[0];

          const freshUrl = await getDownloadURL(latestLogo.item);
          localStorage.setItem(
            cacheKey,
            JSON.stringify({ url: freshUrl, timestamp: Date.now(), latest: latestLogo.timeCreated.getTime() })
          );
          if (mounted) setLogoUrl(freshUrl);
        } else {
          localStorage.removeItem(cacheKey);
          if (mounted) setLogoUrl(null);
        }
      } catch {
        if (mounted) setLogoUrl(null);
      }
    })();

    return () => { mounted = false; };
  }, [merchantId]);

  return logoUrl ? (
    <img
      loading="lazy"
      src={logoUrl}
      alt="Logo du commerçant"
      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }}
    />
  ) : null;
};

// ========================= Styles (copie alignée avec ClientDocs pour carrés) =========================
const pageStyles = `
.merchant-docs-inner{
  width:100%;
  max-width:var(--eb-content-max);
  min-width:0;
}
.merchant-docs-inner .eb-page-title{ margin:0; text-align:left; }
.merchant-docs-stage{
  display:grid;
  grid-template-columns:repeat(auto-fit,minmax(min(100%,440px),1fr));
  gap:20px;
  align-items:stretch;
}

/* PANNEAUX (alignés sur ClientDocs) */
.client-docs-panel{
  position:relative;
  min-width:0;
  background:var(--eb-surface);
  border-radius:var(--eb-radius-lg);
  padding:24px;
  border:1px solid var(--eb-border);
  box-shadow:var(--eb-shadow-sm);
  display:flex;
  flex-direction:column;
  gap:20px;
}
.client-docs-panelHeader{ display:flex; align-items:flex-start; justify-content:space-between; gap:12px; }
.client-docs-panelTitle{ display:flex; flex-direction:column; gap:2px; min-width:0; }
.client-docs-panelTitle span{ font-size:12.5px; font-weight:600; letter-spacing:0.06em; text-transform:uppercase; color:var(--eb-subtle); }
.client-docs-panelTitle h2{ margin:0; text-align:left; font-size:20px; font-weight:700; color:var(--eb-text); letter-spacing:-0.015em; }
.client-docs-actionPill{
  flex-shrink:0; min-height:36px; padding:0 14px; border-radius:var(--eb-radius-sm);
  border:1px solid var(--eb-border-strong); background:var(--eb-surface); color:var(--eb-text-2);
  box-shadow:var(--eb-shadow-xs); font-weight:600; font-size:13.5px; line-height:1; cursor:pointer;
  white-space:nowrap; display:inline-flex; align-items:center; gap:8px;
  transition:background-color .15s ease, color .15s ease;
}
.client-docs-actionPill svg{ color:var(--eb-brand); }
.client-docs-actionPill:hover{ background:var(--eb-surface-2); color:var(--eb-text); }
.client-docs-actionPill:focus-visible{ outline:none; box-shadow:var(--eb-ring); }
.client-docs-panelBody{ display:flex; flex-direction:column; gap:12px; min-width:0; }
.client-docs-panelBody--rail{ margin:0 -24px; padding:0 24px; overflow:hidden; }
.client-docs-railTrack{
  display:flex; gap:12px; overflow-x:auto; padding:2px 2px 10px;
  scroll-snap-type:x mandatory; -webkit-overflow-scrolling:touch;
  scrollbar-width:thin; scrollbar-color:var(--eb-border-strong) transparent;
}
.client-docs-railTrack::-webkit-scrollbar{ height:6px; }
.client-docs-railTrack::-webkit-scrollbar-track{ background:transparent; }
.client-docs-railTrack::-webkit-scrollbar-thumb{ background:var(--eb-border-strong); border-radius:3px; }

/* CARTES FIDÉLITÉ */
.client-docs-loyaltyCard{
  flex:0 0 auto; min-width:250px; max-width:320px; scroll-snap-align:start;
  border-radius:var(--eb-radius); padding:14px 16px; border:1px solid var(--eb-border);
  background:var(--eb-surface); display:flex; gap:14px; align-items:center; text-align:left;
  cursor:pointer; color:var(--eb-text);
  transition:border-color .15s ease, box-shadow .15s ease, background-color .15s ease;
}
.client-docs-loyaltyCard:hover{ border-color:var(--eb-brand-300); box-shadow:var(--eb-shadow-md); }
.client-docs-loyaltyCard:focus-visible{ outline:none; box-shadow:var(--eb-ring); }
.client-docs-loyaltyAvatar{
  width:52px; height:52px; border-radius:50%; background:var(--eb-brand-50); border:1px solid var(--eb-border);
  display:flex; align-items:center; justify-content:center; overflow:hidden; flex-shrink:0;
}
.client-docs-loyaltyInfo{ display:flex; flex-direction:column; gap:2px; min-width:0; flex:1; }
.client-docs-loyaltyInfo h3{
  margin:0; text-align:left; font-size:15px; font-weight:600; color:var(--eb-text); letter-spacing:-0.01em;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
}
.client-docs-loyaltyMeta p{ margin:0; font-size:13.5px; color:var(--eb-muted); font-weight:500; }

/* LISTE DOCUMENTS */
.client-docs-panelBody--stack{ gap:8px; }
.client-docs-invoiceList{ display:flex; flex-direction:column; gap:8px; }
.client-docs-invoiceRow{
  display:flex; align-items:center; gap:14px; width:100%; padding:10px 12px;
  border-radius:var(--eb-radius); border:1px solid var(--eb-border); background:var(--eb-surface);
  text-align:left; cursor:pointer; color:var(--eb-text);
  transition:border-color .15s ease, background-color .15s ease;
}
.client-docs-invoiceRow:hover{ background:var(--eb-surface-2); border-color:var(--eb-border-strong); }
.client-docs-invoiceRow:focus-visible{ outline:none; box-shadow:var(--eb-ring); }
.client-docs-invoicePreview{
  width:52px; height:52px; border-radius:var(--eb-radius-sm); overflow:hidden; flex-shrink:0;
  background:var(--eb-surface-2); border:1px solid var(--eb-border);
}
.client-docs-invoicePreview img{ width:100%; height:100%; object-fit:cover; display:block; }
.client-docs-invoiceCopy{ display:flex; flex-direction:column; gap:2px; min-width:0; flex:1; }
.client-docs-invoiceTitle{ font-size:14.5px; font-weight:600; color:var(--eb-text); line-height:1.3; white-space:nowrap; text-overflow:ellipsis; overflow:hidden; }
.client-docs-invoiceSubtitle{ font-size:13px; color:var(--eb-muted); white-space:nowrap; text-overflow:ellipsis; overflow:hidden; }
.client-docs-invoiceChevron{ font-size:22px; color:var(--eb-subtle); line-height:1; transition:transform .15s ease, color .15s ease; }
.client-docs-invoiceRow:hover .client-docs-invoiceChevron{ transform:translateX(2px); color:var(--eb-brand); }

/* ÉTAT VIDE */
.client-docs-empty{
  padding:28px 20px; border-radius:var(--eb-radius); background:var(--eb-surface-2);
  border:1px dashed var(--eb-border-strong); color:var(--eb-muted); font-size:14px; line-height:1.5; text-align:center;
}

/* PIED DE PANNEAU */
.client-docs-panelFooter{
  margin-top:auto;
  display:flex; justify-content:flex-end; gap:12px; flex-wrap:wrap; align-items:center;
  padding-top:16px; border-top:1px solid var(--eb-border);
}
.client-docs-cta{
  display:inline-flex; align-items:center; justify-content:center; min-height:40px; padding:0 14px;
  border-radius:var(--eb-radius-sm); font-size:14px; font-weight:600; border:1px solid transparent;
  cursor:pointer; transition:background-color .15s ease, color .15s ease;
}
.client-docs-cta--ghost{ background:transparent; color:var(--eb-brand); margin-right:-14px; }
.client-docs-cta--ghost::after{ content:'→'; margin-left:8px; transition:transform .15s ease; }
.client-docs-cta--ghost:hover:not(:disabled){ background:var(--eb-brand-50); }
.client-docs-cta--ghost:hover:not(:disabled)::after{ transform:translateX(2px); }
.client-docs-cta:disabled{ opacity:0.45; cursor:not-allowed; }
.client-docs-cta:focus-visible{ outline:none; box-shadow:var(--eb-ring); }

@media (min-width:1024px){
  .client-docs-loyaltyCard{ flex:1 1 250px; max-width:none; }
  .client-docs-railTrack--triangle{
    display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:12px; overflow:visible; padding-bottom:2px;
  }
  .client-docs-loyaltyCard--triangleTop{ min-width:0; max-width:none; width:100%; }
  .client-docs-loyaltyCard--triangleBottom{ grid-column:1 / -1; min-width:0; max-width:none; width:100%; }
}
@media (max-width:768px){
  .client-docs-panel{ padding:20px 16px; gap:16px; }
  .client-docs-panelBody--rail{ margin:0 -16px; padding:0 16px; }
  .client-docs-panelFooter{ padding-top:12px; }
}
@media (max-width:480px){
  .client-docs-loyaltyCard{ min-width:240px; max-width:82vw; }
}
`;

// ========================= Helpers affichage fidélité (identiques à ClientDocs) =========================
const isRewardImminent = (item) => {
  if (!item) return false;
  if (item.Type === 'Fidbycount') {
    const multiple = Number(item.multiple || 0);
    const compteur = Number(item.compteur || 0);
    return multiple > 0 && multiple - compteur === 1;
  }
  if (item.Type === 'Fidbypoints') {
    const points = Number(item.points || 0);
    const threshold = Number(item.points1 || 0);
    return threshold > 0 && points >= threshold;
  }
  return false;
};

const getCardStyle = (item) =>
  isRewardImminent(item)
    ? { background: 'var(--eb-warning-bg)', borderColor: '#fde3b0' }
    : {};

const getCardText = (item) => {
  if (item.Type === 'Fidbycount') {
    const eligible = isRewardImminent(item);
    return (
      <>
        <p style={{ margin: 0 }}>{item.compteur} / {item.multiple} passages</p>
        {eligible && <p style={{ margin: '2px 0 0 0', color: 'var(--eb-warning)', fontWeight: 600 }}>Gain au prochain passage !</p>}
      </>
    );
  }
  if (item.Type === 'Fidbypoints') {
    const eligible = isRewardImminent(item);
    return (
      <>
        <p style={{ margin: 0, fontWeight: 600, color: 'var(--eb-text-2)' }}>{item.points} points</p>
        {eligible && <p style={{ margin: '2px 0 0 0', color: 'var(--eb-warning)', fontWeight: 600 }}>Gain disponible !</p>}
      </>
    );
  }
  return null;
};

// ========================= Cache docs (local uniquement, plus d'index Firestore) =========================
const DOCS_INDEX_TTL_MS = 5 * 60 * 1000; // 5 minutes (cache local)
const docsCacheKey = (uid) => `merchant_docs_index_${uid}`;
const nowMs = () => Date.now();

const normalizeDocItem = (item = {}) => ({
  name: item.name || '',
  url: item.url || '',
  date: item.date || '',
  timestamp: item.timestamp || 0,
  employeePseudo: item.employeePseudo || '',
  title: item.title || '',
  ownerUid: item.ownerUid || '',
  metadata: item.metadata || {},
});

const serializeDocsPayload = (payload = {}) => {
  const items = Array.isArray(payload.items) ? payload.items.map(normalizeDocItem) : [];
  return { items, updatedAt: payload.updatedAt || nowMs() };
};

const loadDocsCache = (uid) => {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(docsCacheKey(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const ts = parsed?.ts || 0;
    if (!ts || nowMs() - ts > DOCS_INDEX_TTL_MS) return null;
    return { ...(parsed.payload || {}), updatedAt: ts };
  } catch {
    return null;
  }
};

const saveDocsCache = (uid, payload) => {
  if (!uid || !payload) return;
  try {
    localStorage.setItem(
      docsCacheKey(uid),
      JSON.stringify({ ts: payload.updatedAt || nowMs(), payload: serializeDocsPayload(payload) })
    );
  } catch {}
};

// =======================================================================================================

const MerchantDocs = () => {
  // eslint-disable-next-line
  const [user, setUser] = useState(null);
  const [photos, setPhotos] = useState([]);
  const navigate = useNavigate();
  // eslint-disable-next-line
  const [invitations, setInvitations] = useState([]);
  // eslint-disable-next-line
  const [showModal, setShowModal] = useState(false);
  const { fetchData, dataCache } = useDataContext();
  // eslint-disable-next-line
  const [data, setData] = useState(null);
  const [photoTitle, setPhotoTitle] = useState('');
  // eslint-disable-next-line
  const [isEditing, setIsEditing] = useState(false);

  const lastForcedFetchUidRef = useRef(null);
  const { photo } = window.location.state || {};
  const [fidelityData, setFidelityData] = useState([]);
  const [isAuthLoaded, setIsAuthLoaded] = useState(false);
  const [docsIndexReady, setDocsIndexReady] = useState(false);

  // caches pour fidélité (opti)
  const merchantMetaCache = useRef({});
  const pendingMetaFetch = useRef(new Set());
  const cardsMap = useRef({});
  const docsSigRef = useRef('');
  const docsTsRef = useRef(0);

  // === Ajout pour l’import (même logique que ClientDocs) ===
  const applyDocsPayload = useCallback((payload) => {
    if (!payload) return;
    const items = Array.isArray(payload.items) ? payload.items : [];
    setPhotos((prev) => {
      const prevSig = JSON.stringify((prev || []).map(normalizeDocItem));
      const nextSig = JSON.stringify(items.map(normalizeDocItem));
      return prevSig === nextSig ? prev : items;
    });
    try {
      localStorage.setItem('userPhotos', JSON.stringify(items));
      localStorage.setItem('userPhotosTs', String(payload.updatedAt || nowMs()));
    } catch {}
  }, []);

const persistDocsIndex = useCallback((uid, payload) => {
  if (!uid || !payload) return;
  const normalized = serializeDocsPayload(payload);
  const sig = JSON.stringify(normalized);
  docsSigRef.current = sig;
  docsTsRef.current = normalized.updatedAt;
  saveDocsCache(uid, normalized);
}, []);

  const fileInputRef = useRef(null);
  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);
  const handleImportInvoice = useCallback(
    async (event) => {
      const input = event?.target;
      const file = input?.files?.[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        alert('Veuillez sélectionner un fichier image (PNG, JPG, HEIC, etc.)');
        if (input) input.value = null;
        return;
      }

      const auth = getAuth();
      const uid = auth.currentUser?.uid;
      if (!uid) {
        alert('Utilisateur non connecté.');
        if (input) input.value = null;
        return;
      }

      try {
        const rawName = file.name.replace(/\.[^/.]+$/, '');
        const cleanTitle = rawName || 'Document importé';

        const storageRef = ref(storage, `${uid}/photos/${file.name}`);
        await uploadBytes(storageRef, file, {
          customMetadata: {
            TakenByPseudo: user?.pseudo || 'Moi',
            title: cleanTitle,
            category: 'perso',
          },
        });

        const [meta, url] = await Promise.all([getMetadata(storageRef), getDownloadURL(storageRef)]);
        const ts = meta.timeCreated ? new Date(meta.timeCreated).getTime() : Date.now();

        const newPhoto = {
          name: file.name,
          url,
          timestamp: ts,
          title: meta.customMetadata?.title || cleanTitle,
          employeePseudo: meta.customMetadata?.TakenByPseudo || 'Moi',
          metadata: meta.customMetadata || {},
        };

        const updated = [newPhoto, ...photos].sort((a, b) => b.timestamp - a.timestamp);
        const payload = { items: updated, updatedAt: nowMs() };
        setPhotos(updated);
        try {
          localStorage.setItem('userPhotos', JSON.stringify(updated));
          localStorage.setItem('userPhotosTs', String(payload.updatedAt));
        } catch {}
        persistDocsIndex(uid, payload);
      } catch (e) {
        console.error('Erreur lors de l’import du document :', e);
        alert('Erreur lors de l’import du document.');
      } finally {
        if (input) input.value = null;
      }
    },
    [photos, user, persistDocsIndex]
  );

  // --------- PHOTOS ---------
  const fetchPhotos = useCallback(async (force = false) => {
    const TTL = DOCS_INDEX_TTL_MS;
    try {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      if (!userId) return;

      if (!force && docsSigRef.current && nowMs() - docsTsRef.current < DOCS_INDEX_TTL_MS) {
        return;
      }

      // 1) Utilise le cache s'il est frais
      const storedPhotos = localStorage.getItem('userPhotos');
      const storedTs = Number(localStorage.getItem('userPhotosTs') || 0);
      let storedPhotosData = [];
      if (storedPhotos) {
        try {
          storedPhotosData = JSON.parse(storedPhotos) || [];
          if (storedPhotosData.length) {
            setPhotos(storedPhotosData);
          }
        } catch {}
      }
      if (storedPhotosData.length && storedTs) {
        const payload = serializeDocsPayload({ items: storedPhotosData, updatedAt: storedTs });
        const sig = JSON.stringify(payload);
        if (!docsSigRef.current) docsSigRef.current = sig;
        if (!docsTsRef.current) docsTsRef.current = payload.updatedAt;
      }
      if (!force && storedTs && Date.now() - storedTs < TTL && storedPhotosData.length) {
        return;
      }

      // 2) Photos du compte entreprise lui-même
      const photosRefUser = ref(storage, `${userId}/photos/`);
      const photoListUser = await listAll(photosRefUser);
      const userPhotos = await Promise.all(
        photoListUser.items.map(async (item) => {
          const [url, metadata] = await Promise.all([
            getDownloadURL(item),
            getMetadata(item).catch(() => null),
          ]);
          const createdAt = metadata?.timeCreated ? new Date(metadata.timeCreated) : new Date();
          const timestamp = createdAt.getTime();
          const formattedDate = createdAt.toLocaleString('fr-FR', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          });
          const employeePseudo = metadata?.customMetadata?.TakenByPseudo || 'Moi';
          const title = metadata?.customMetadata?.title || null;

          return {
            name: item.name,
            url,
            date: formattedDate,
            timestamp,
            employeePseudo,
            title,
            ownerUid: userId,
            metadata: metadata?.customMetadata || {},
          };
        })
      );

      // 3) Photos partagées par les salariés (Firestore → Storage)
      const sharedEntries = await listCompanyShares(userId);
      const sharedPhotos = await Promise.all(
        sharedEntries.map(async (entry) => {
          const fileName = entry.fileName || entry.name;
          if (!fileName) return null;
          const storagePath = entry.storagePath || `${entry.ownerUid}/photos/${fileName}`;
          const photoRef = ref(storage, storagePath);
          try {
            const [url, metadata] = await Promise.all([
              getDownloadURL(photoRef),
              getMetadata(photoRef).catch(() => null),
            ]);
            const createdAt = entry.timestamp
              ? new Date(entry.timestamp)
              : metadata?.timeCreated
                ? new Date(metadata.timeCreated)
                : new Date();
            const timestamp = createdAt.getTime();
            const formattedDate = createdAt.toLocaleString('fr-FR', {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            });
            const employeePseudo =
              entry.takenByPseudo ||
              metadata?.customMetadata?.TakenByPseudo ||
              'Inconnu';
            const title = entry.title || metadata?.customMetadata?.title || null;

            return {
              name: fileName,
              url,
              date: formattedDate,
              timestamp,
              employeePseudo,
              title,
              ownerUid: entry.ownerUid || null,
              metadata: metadata?.customMetadata || {},
            };
          } catch {
            return null;
          }
        })
      );

      const allPhotos = [...userPhotos, ...sharedPhotos.filter(Boolean)];
      const sorted = allPhotos.sort((a, b) => b.timestamp - a.timestamp);
      const payload = { items: sorted, updatedAt: nowMs() };
      applyDocsPayload(payload);
      persistDocsIndex(userId, payload);
    } catch (error) {
      console.error("Erreur lors de la récupération des photos :", error);
      alert("Impossible de récupérer les photos.");
    }
  }, [applyDocsPayload, persistDocsIndex]);

  // Chargement docs : cache local uniquement (index Firestore supprimé)
  useEffect(() => {
    setDocsIndexReady(false);
    if (!user?.uid) return;
    const uid = user.uid;

    const cached = loadDocsCache(uid);
    if (cached) {
      applyDocsPayload(cached);
      docsSigRef.current = JSON.stringify(serializeDocsPayload(cached));
      docsTsRef.current = cached.updatedAt || nowMs();
    }

    // Plus d'index distant : on passe directement en ready après le cache
    setDocsIndexReady(true);
  }, [user?.uid, applyDocsPayload]);

  // --------- AUTH + DATA ---------
  useEffect(() => {
    const auth = getAuth();
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userId = user?.uid;
        if (userId) {
          const userDocRef = doc(firestore, 'users', userId);
          const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
            const userData = docSnapshot.data();
            setUser({ ...userData, pseudo: userData?.pseudo });

            if (!userData?.pseudo) {
              setShowModal(true);
            }

            fetchInvitations(userData?.email);
            fetchPhotos();

            fetchData('merchantInvoices')
              .then(fetchedData => {
                setData(fetchedData);
                if (fetchedData && fetchedData.length > 0) {
                  localStorage.setItem('merchantInvoices', JSON.stringify(fetchedData));
                }
              })
              .catch(error => {
                console.error('Erreur lors de la récupération des données merchantInvoices :', error);
              });
          });

          return () => unsubscribe();
        }
      } else {
        navigate('/logingeneral');
      }
    });

    if (photo) {
      if (!isEditing) {
        const storedPhotos = JSON.parse(localStorage.getItem('userPhotos') || '[]');
        const localPhoto = storedPhotos?.find((p) => p.name === photo.name);

        if (localPhoto && localPhoto.metadata?.title) {
          setPhotoTitle(localPhoto.metadata.title);
        } else {
          if (photo.metadata && photo.metadata.title) {
            setPhotoTitle(photo.metadata.title);
          }
        }

        const updateMetadataFromFirebase = async () => {
          const photoRef = ref(storage, `${getAuth().currentUser?.uid}/photos/${photo.name}`);
          try {
            const metadata = await getMetadata(photoRef);
            if (metadata.customMetadata?.title && metadata.customMetadata.title !== photoTitle) {
              setPhotoTitle(metadata.customMetadata.title);
            }
          } catch (error) {
            console.error('Erreur lors de la récupération des métadonnées depuis Firebase :', error);
          }
        };

        if (!localPhoto || !localPhoto.metadata?.title) {
          updateMetadataFromFirebase();
        }
      }
    }

    return () => unsubscribeAuth();
  }, [navigate, fetchPhotos, fetchData, dataCache, photo, isEditing, photoTitle]);

  useEffect(() => {
    if (!user?.uid || !docsIndexReady) return;
    if (lastForcedFetchUidRef.current === user.uid) return;
    lastForcedFetchUidRef.current = user.uid;
    fetchPhotos(true);
  }, [user?.uid, fetchPhotos, docsIndexReady]);

  useEffect(() => {
    setIsAuthLoaded(true);
  }, []);

  // --------- OPTI FIDÉLITÉ (écoute ciblée) ---------
  const upsertCard = useCallback(async (merchantId, clientData) => {
    if (!merchantMetaCache.current[merchantId] && !pendingMetaFetch.current.has(merchantId)) {
      pendingMetaFetch.current.add(merchantId);
      try {
        // Lecture autorisée: entreprises (get/list limité) → pseudo
        let pseudo = 'Inconnu';
        const entSnap = await getDocs(
          query(collection(firestore, 'entreprises'), where('userId', '==', merchantId), fsLimit(1))
        );
        if (!entSnap.empty) {
          const d = entSnap.docs[0].data() || {};
          pseudo =
            d.pseudo ||
            d.entreprise_name ||
            d.companyName ||
            d.name ||
            d.brand ||
            d.alias ||
            d.displayName ||
            'Inconnu';
        }
        merchantMetaCache.current[merchantId] = {
          Type: 'Fidbycount',
          multiple: 0,
          gift: '',
          points1: 0,
          pseudo,
        };
      } catch {
        merchantMetaCache.current[merchantId] = {
          Type: 'Fidbycount', multiple: 0, gift: '', points1: 0, pseudo: 'Inconnu',
        };
      } finally {
        pendingMetaFetch.current.delete(merchantId);
      }
    }

    const meta = merchantMetaCache.current[merchantId] || {};
    cardsMap.current[merchantId] = {
      merchantId,
      merchantPseudo: meta.pseudo || 'Inconnu',
      Type: meta.Type || 'Fidbycount',
      multiple: meta.multiple || 0,
      compteur: clientData?.compteur || 0,
      points: clientData?.points || 0,
      points1: meta.points1 || 0,
    };
    setFidelityData(Object.values(cardsMap.current));
  }, []);

  // === Listener collectionGroup sur les cartes où je suis client (clientUid == uid ou docId == uid)
  useEffect(() => {
    if (!isAuthLoaded) return;
    const auth = getAuth();

    let stopFidelityA = null;

    const cleanupAll = () => {
      cardsMap.current = {};
      setFidelityData([]);
    };

    const stopAuth = onAuthStateChanged(auth, async (currentUser) => {
      if (stopFidelityA) { stopFidelityA(); stopFidelityA = null; }
      cleanupAll();

      if (!currentUser) return;
      const uid = currentUser.uid;

      const cgList = collectionGroup(firestore, 'list');
      const qClientField = query(cgList, where('clientUid', '==', uid));

      const handleSnap = (snap) => {
        snap.docChanges().forEach((change) => {
          const merchantId = change.doc.ref.parent?.parent?.id;
          if (!merchantId) return;
          if (change.type === 'removed') {
            delete cardsMap.current[merchantId];
            setFidelityData(Object.values(cardsMap.current));
            return;
          }
          upsertCard(merchantId, change.doc.data() || {});
        });
      };

      stopFidelityA = onSnapshot(qClientField, handleSnap, (err) =>
        console.error('fidelity list cg listener error:', err)
      );
    });

    return () => {
      if (stopFidelityA) stopFidelityA();
      cleanupAll();
      stopAuth();
    };
  }, [isAuthLoaded, upsertCard]);

  const fetchInvitations = async (email) => {
    const entrepriseRef = collection(firestore, 'entreprises');
    // Règles Firestore exigent un limit <= 50 sur les listings
    const q = query(entrepriseRef, where('employeeEmail', '==', email), where('status', '==', 'pending'), fsLimit(50));
    const querySnapshot = await getDocs(q);
    const fetchedInvitations = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setInvitations(fetchedInvitations);
  };

  if (!isAuthLoaded) return <></>;

  const goToTransfer = () => navigate('/transfertfid');

  // Helpers d'affichage documents (mêmes raccourcis que ClientDocs)
  const formatPhotoTitle = (title) => {
    if (!title) return 'Sans titre';
    const trimmed = String(title).trim();
    return trimmed.length > 18 ? `${trimmed.slice(0, 18)}…` : trimmed;
  };
  const getPhotoAuthor = (p) => (p?.employeePseudo ? `Par ${p.employeePseudo}` : 'Document importé');

  // Tri fidélité façon ClientDocs (met en avant récompense imminente)
  const sortLoyaltyData = (data) => {
    const withFlag = (data || []).map((d) => ({ ...d, __imminent: isRewardImminent(d) }));
    const byImminent = (a, b) => (b.__imminent === a.__imminent ? 0 : b.__imminent ? 1 : -1);
    return withFlag.sort(byImminent);
  };
  const sortedFid = sortLoyaltyData(fidelityData);
  const previewFid = sortedFid.slice(0, 3);

  // === Ajout pour correspondre EXACTEMENT au layout ClientDocs sur desktop ===
  const triangleLayout = previewFid.length === 3;

  return (
    <>
      <style>{pageStyles}</style>

      <div className="dashboard-container merchant-docs-dashboard">
        <MerchantNavbar />

        <div className="merchant-docs-inner">
          <header className="eb-page-header">
            <div>
              <h1 className="eb-page-title">Documents</h1>
              <p className="eb-page-subtitle">Vos programmes de fidélité et vos factures</p>
            </div>
          </header>

          {/* ==== STAGE : deux panneaux identiques à ClientDocs (FID & DOCS) ==== */}
          <section className="merchant-docs-stage">
            {/* --- Panneau Fidélité (même design que ClientDocs) --- */}
            <article
              className="client-docs-panel"
              role="region"
              aria-label="Votre fidélité"
            >
              <header className="client-docs-panelHeader">
                <div className="client-docs-panelTitle">
                  <span>Programmes</span>
                  <h2>Fidélité</h2>
                </div>
                <button
                  type="button"
                  className="client-docs-actionPill"
                  onClick={(e) => { e.stopPropagation(); goToTransfer(); }}
                  title="Partager mes points"
                >
                  Partager mes points
                </button>
              </header>

              {fidelityData.length > 0 ? (
                <div className="client-docs-panelBody client-docs-panelBody--rail">
                  <div className={`client-docs-railTrack ${triangleLayout ? 'client-docs-railTrack--triangle' : ''}`}>
                    {previewFid.map((item, index) => (
                      <button
                        key={`${item.merchantId}-${index}`}
                        type="button"
                        className={`client-docs-loyaltyCard ${triangleLayout ? (index === 2 ? 'client-docs-loyaltyCard--triangleBottom' : 'client-docs-loyaltyCard--triangleTop') : ''}`}
                        style={getCardStyle(item)}
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/merchantfidviewer/${item.merchantId}`);
                        }}
                        aria-label={item.merchantPseudo ? `Voir ${item.merchantPseudo}` : 'Voir le programme'}
                        title={item.merchantPseudo || 'Programme fidélité'}
                      >
                        <div className="client-docs-loyaltyAvatar">
                          <MerchantLogo merchantId={item.merchantId} />
                        </div>
                        <div className="client-docs-loyaltyInfo">
                          <h3>{item.merchantPseudo}</h3>
                          <div className="client-docs-loyaltyMeta">
                            {getCardText(item)}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="client-docs-panelBody">
                  <div className="client-docs-empty">Réalisez votre premier scan</div>
                </div>
              )}

              <footer className="client-docs-panelFooter">
                <button
                  type="button"
                  className="client-docs-cta client-docs-cta--ghost"
                  onClick={(e) => { e.stopPropagation(); navigate('/merchantfid'); }}
                  disabled={fidelityData.length === 0}
                >
                  Voir tous les programmes
                </button>
              </footer>
            </article>

            {/* --- Panneau Documents (même design que ClientDocs) --- */}
            <article
              className="client-docs-panel"
              role="region"
              aria-label="Vos documents"
            >
              <header className="client-docs-panelHeader">
                <div className="client-docs-panelTitle">
                  <span>Documents</span>
                  <h2>Factures</h2>
                </div>
                {/* === Bouton Importer ajouté, même emplacement que ClientDocs === */}
                <button
                  type="button"
                  className="client-docs-actionPill"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleImportClick();
                  }}
                >
                  <FaUpload aria-hidden="true" />
                  <span>Importer</span>
                </button>
              </header>

              {/* input fichier caché (mêmes attributs que ClientDocs) */}
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleImportInvoice}
              />

              {photos.length > 0 ? (
                <div className="client-docs-panelBody client-docs-panelBody--stack">
                  <div className="client-docs-invoiceList">
                    {photos.slice(0, 3).map((p, idx) => (
                      <button
                        key={`${p.name}-${p.timestamp || idx}`}
                        type="button"
                        className="client-docs-invoiceRow"
                        title={p.title || 'Sans titre'}
                        onClick={(e) => {e.stopPropagation(); navigate('/merchantphotoviewer', { state: { photo: p } }); }}
                      >
                        <div className="client-docs-invoicePreview">
                          <img loading="lazy" src={p.url} alt={p.title || 'Document'} />
                        </div>
                        <div className="client-docs-invoiceCopy">
                          <span className="client-docs-invoiceTitle">
                            {formatPhotoTitle(p.metadata?.title || p.title)}
                          </span>
                          <span className="client-docs-invoiceSubtitle">
                            {getPhotoAuthor(p)}
                          </span>
                        </div>
                        <span className="client-docs-invoiceChevron" aria-hidden="true">›</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="client-docs-panelBody">
                  <div className="client-docs-empty">
                    Réalisez votre premier scan pour enregistrer vos documents
                  </div>
                </div>
              )}

              <footer className="client-docs-panelFooter">
                <button
                  type="button"
                  className="client-docs-cta client-docs-cta--ghost"
                  onClick={(e) => { e.stopPropagation(); navigate('/merchantinvoices'); }}
                  disabled={photos.length === 0}
                >
                  Voir tous les documents
                </button>
              </footer>
            </article>
          </section>
        </div>
      </div>
    </>
  );
};

export default MerchantDocs;
